import { useSendOTPMutation } from '@zonofi/seller-rtk-store';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthChannel } from '@zonofi/seller-rtk-store';
import { CountryObject } from '@zonofi/i18n';
import { isPhoneNumberValid } from '@zonofi/common';

interface useSplashScreenPhoneProps {
  onUpdateValue?: (key: string, value: string | boolean) => void;
  slides: {
    description: JSX.Element;
    heading: JSX.Element;
    imageUrl: string;
  }[];
}

export const useSplashScreenPhone = ({
  onUpdateValue,
  slides,
}: useSplashScreenPhoneProps) => {
  const navigate = useNavigate();
  const totalSlides = slides.length;
  const mobileInputRef = useRef<HTMLDivElement | null>(null);
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [inputErrors, setInputErrors] = useState<{
    isError: boolean;
    errorMsg: string;
  }>({
    isError: false,
    errorMsg: '',
  });
  const [loginVal, setLoginVal] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<CountryObject>(null);
  const [countryDrawerOpen, setCountryDrawerOpen] = useState<boolean>(false);
  const [sendOtpReq, sendOtpRes] = useSendOTPMutation();
  const containerRef = useRef<HTMLDivElement>(null);
  const loaderRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mediaRefs = useRef<(HTMLImageElement | null)[]>([]);

  const validateInput = useCallback(
    (val: string): boolean => {
      // Ensure input is numeric and exactly 10 digits
      const isNumeric = /^\d+$/.test(val);
      const isValid =
        isNumeric && isPhoneNumberValid(val, selectedCountry?.code);

      setInputErrors({
        isError: !isValid,
        errorMsg: !isNumeric
          ? 'Only numbers allowed'
          : !isValid
            ? 'Invalid Mobile Number'
            : '',
      });

      return isValid;
    },
    [selectedCountry, setInputErrors]
  );

  const preloadMedia = (index: number) => {
    const media = mediaRefs.current[index];
    if (media && !media.classList.contains('loaded')) {
      const src = media.src;
      media.src = '';
      media.src = src;
    }
  };

  const updateSlide = (index: number) => {
    setCurrentSlide(index);
    preloadMedia(index + 1);
  };

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      updateSlide(currentSlide + 1);
    }
  };

  const previousSlide = () => {
    if (currentSlide > 0) updateSlide(currentSlide - 1);
  };

  const navigateToLogin = () => {
    if (onUpdateValue) {
      onUpdateValue('phoneNumber', '');
    }
    navigate('/login');
  };

  const onChange = (e: { target: { value: string } }) => {
    // Only allow numeric input - completely reject non-numeric values
    const inputValue = e.target.value;

    // If input contains any non-numeric characters, show error and don't update
    if (inputValue.length > 0 && !/^\d+$/.test(inputValue)) {
      setInputErrors({
        isError: true,
        errorMsg: 'Only numbers allowed',
      });
      return;
    }

    // Only allow numeric input
    const value = inputValue.replace(/\D/g, '');

    // Update parent component
    onUpdateValue?.('phoneNumber', `${value}`);

    // Clear or validate error message
    if (value.length === 0) {
      setInputErrors({ isError: false, errorMsg: '' });
    }

    setLoginVal(value);
  };

  const onGenerateOtp = useCallback(() => {
    const isValid = validateInput(loginVal || '');

    const payload = {
      authChannel: 'mobile' as AuthChannel,
      mobile: `${selectedCountry?.dial_code}${loginVal}`,
      doCreateUser: true,
    };

    onUpdateValue?.('phoneNumber', `${loginVal}`);

    if (isValid) {
      sendOtpReq({ ...payload });
    }
  }, [loginVal, validateInput, onUpdateValue, sendOtpReq]);

  const countryObjectToLocal = (country: CountryObject) => {
    const countryString = JSON.stringify(country);
    localStorage.setItem('selectedCountry', countryString);
  };

  const openCountriesDrawer = () => {
    setCountryDrawerOpen(true);
  };

  const closeCountriesDrawer = () => {
    setCountryDrawerOpen(false);
  };

  useEffect(() => {
    if (sendOtpRes.isSuccess) {
      if (sendOtpRes?.data?.mobile?.resendAttemptsLeft > 0) {
        navigate('/verifyOtp');
      } else if (sendOtpRes?.data?.mobile?.message) {
        setInputErrors({
          isError: true,
          errorMsg: sendOtpRes?.data?.mobile?.message,
        });
      } else if (sendOtpRes?.data?.message) {
        setInputErrors({
          isError: true,
          errorMsg: sendOtpRes?.data?.message,
        });
      }
    }
  }, [sendOtpRes, navigate]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow navigation keys, backspace, delete, etc.
    if (
      e.key === 'Backspace' ||
      e.key === 'Delete' ||
      e.key === 'Tab' ||
      e.key === 'Enter' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown'
    ) {
      return;
    }

    // Only allow numeric input
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
      setInputErrors({
        isError: true,
        errorMsg: 'Only numbers allowed',
      });
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Enter':
          onGenerateOtp();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onGenerateOtp]);

  useEffect(() => {
    const country = localStorage.getItem('selectedCountry');
    if (country) {
      const countryObject = JSON.parse(country);
      setSelectedCountry(countryObject);
    } else {
      setSelectedCountry({
        name: 'India',
        flag: '🇮🇳',
        code: 'IN',
        dial_code: '+91',
        currency_code: 'INR',
        time_zones: ['Asia/Kolkata'],
      });
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const anchorEl = mobileInputRef.current;
      const drawerEl = document.querySelector('.countries-list');

      if (
        countryDrawerOpen &&
        anchorEl &&
        !anchorEl.contains(event.target as Node) &&
        drawerEl &&
        !drawerEl.contains(event.target as Node)
      ) {
        closeCountriesDrawer();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [countryDrawerOpen]);

  useEffect(() => {
    const container = containerRef.current;
    let startX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        diff > 0 ? nextSlide() : previousSlide();
      }
    };

    container?.addEventListener('touchstart', handleTouchStart);
    container?.addEventListener('touchend', handleTouchEnd);

    return () => {
      container?.removeEventListener('touchstart', handleTouchStart);
      container?.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentSlide]);

  return {
    closeCountriesDrawer,
    containerRef,
    countryDrawerOpen,
    countryObjectToLocal,
    currentSlide,
    inputErrors,
    isSendingOtp: sendOtpRes?.isLoading,
    loaderRefs,
    loginVal,
    mediaRefs,
    mobileInputRef,
    navigateToLogin,
    nextSlide,
    onChange,
    onGenerateOtp,
    onKeyDown,
    openCountriesDrawer,
    previousSlide,
    selectedCountry,
    setSelectedCountry,
  };
};
